import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Bot, Calculator, Loader2 } from "lucide-react";
import { Group, Product } from "@/lib/types";

interface OrderSuggestionsDialogProps {
  groups: Group[];
  products: Product[];
  trigger?: React.ReactNode;
}

const OrderSuggestionsDialog = ({ groups, products, trigger }: OrderSuggestionsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [mode, setMode] = useState<"quantity" | "duration">("duration");
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [targetQuantity, setTargetQuantity] = useState<string>("");
  const [targetDuration, setTargetDuration] = useState<string>("3");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string>("");
  const { toast } = useToast();

  const selectedGroupObj = groups.find(g => g.id === selectedGroup);
  const groupProducts = selectedGroupObj 
    ? products.filter(p => selectedGroupObj.productIds.includes(p.id))
    : [];

  const handleGetSuggestions = async () => {
    if (!selectedGroup || !targetDuration) {
      toast({
        title: "Missing Information",
        description: "Please select a group and duration",
        variant: "destructive",
      });
      return;
    }

    if (mode === "quantity" && (!selectedProduct || !targetQuantity)) {
      toast({
        title: "Missing Information",
        description: "Please select a product and target quantity",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const body: any = {
        groupId: selectedGroup,
        targetDuration: parseInt(targetDuration),
      };

      if (mode === "quantity") {
        body.targetProductId = selectedProduct;
        body.targetQuantity = parseInt(targetQuantity);
      }

      const { data, error } = await supabase.functions.invoke('ai-order-suggestions', {
        body
      });

      if (error) throw error;

      setSuggestions(data.suggestions);
      toast({
        title: "AI Suggestions Generated",
        description: "Order recommendations are ready",
      });
    } catch (error: any) {
      console.error('Error getting AI suggestions:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to get AI suggestions",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedGroup("");
    setMode("duration");
    setSelectedProduct("");
    setTargetQuantity("");
    setTargetDuration("3");
    setSuggestions("");
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      setOpen(newOpen);
      if (!newOpen) resetForm();
    }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="gap-2">
            <Bot className="h-4 w-4" />
            AI Order Assistant
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            AI Order Suggestions
          </DialogTitle>
          <DialogDescription>
            Get AI-powered recommendations for ordering supplies based on your usage patterns
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Order Parameters</CardTitle>
              <CardDescription>
                Choose how to calculate your order quantities
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="group">Select Group</Label>
                <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map(group => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedGroup && (
                <>
                  <div>
                    <Label>Calculation Mode</Label>
                    <Select value={mode} onValueChange={(v) => setMode(v as "quantity" | "duration")}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="duration">Duration Only</SelectItem>
                        <SelectItem value="quantity">Target Quantity</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {mode === "quantity" && (
                    <>
                      <div>
                        <Label htmlFor="product">Target Product</Label>
                        <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a product" />
                          </SelectTrigger>
                          <SelectContent>
                            {groupProducts.map(product => (
                              <SelectItem key={product.id} value={product.id}>
                                {product.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="quantity">Target Quantity</Label>
                        <Input
                          id="quantity"
                          type="number"
                          value={targetQuantity}
                          onChange={(e) => setTargetQuantity(e.target.value)}
                          placeholder="How many units?"
                          min="1"
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              <div>
                <Label htmlFor="duration">Duration (months)</Label>
                <Select value={targetDuration} onValueChange={setTargetDuration}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 month</SelectItem>
                    <SelectItem value="2">2 months</SelectItem>
                    <SelectItem value="3">3 months</SelectItem>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleGetSuggestions} 
                disabled={loading || !selectedGroup || (mode === "quantity" && (!selectedProduct || !targetQuantity))}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating Suggestions...
                  </>
                ) : (
                  <>
                    <Bot className="h-4 w-4 mr-2" />
                    Get AI Suggestions
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>AI Recommendations</CardTitle>
              <CardDescription>
                Based on your last 30 days of usage data
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading && (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              )}
              
              {suggestions && !loading && (
                <div className="prose prose-sm max-w-none">
                  <div className="whitespace-pre-wrap bg-muted p-4 rounded-lg">
                    {suggestions}
                  </div>
                </div>
              )}
              
              {!suggestions && !loading && (
                <div className="text-center text-muted-foreground p-8">
                  <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Configure your order parameters and get AI-powered suggestions</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderSuggestionsDialog;